import axios from "axios";
import { ChatAgentResponse } from "../../models/ChatWindowModels";
import { PortalMortgageDocumentModel } from "../../models/PortalMortgageDocumentModel";

// Use the global axios instance so the RequestInterceptor's
// Bearer token and baseURL are applied automatically.
// All routes are relative to REACT_APP_API_URL (e.g. https://localhost:7200/api).

export class ChatWindowService {
    static async startSession(name: string): Promise<{ applicationId: number }> {
        const res = await axios.post("/agentchat/start", { name });
        return res.data;
    }

    static async sendMessage(
        message: string,
        applicationId: number,
        userName: string
    ): Promise<ChatAgentResponse> {
        const res = await axios.post("/agentchat/message", { applicationId, userName, message });
        return res.data;
    }

    static async summariseProfile(
        variant: string,
        mortgageJson: object
    ): Promise<string> {
        const res = await axios.post("/agentchat/summarise", {
            variant,
            mortgageJson,
        });
        const data = res.data;
        if (typeof data === "string") return data;
        return data.summary || data.message || data.text || JSON.stringify(data);
    }

    static async uploadDocument(
        file: File,
        applicationId: number,
        userName: string
    ): Promise<ChatAgentResponse> {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("applicationId", applicationId.toString());
        formData.append("userName", userName);
        const res = await axios.post("/agentchat/upload", formData);
        return res.data;
    }

    /**
     * Call the existing SP/UpdateCommentPin endpoint to submit a PIN for an encrypted PDF.
     * Same endpoint DocumentPill uses.
     */
    static async updateCommentPin(
        mortgageId: string,
        filename: string,
        uploadId: string,
        pin: string,
        mortgageDocumentId: string,
    ): Promise<void> {
        await axios.post("/SP/UpdateCommentPin", null, {
            params: {
                mortgageId,
                filename,
                uploadId,
                custComment: "",
                pin,
                mortgageDocumentId,
            },
        });
    }

    /**
     * Mark a mortgage document as uploaded in Dataverse.
     * Same endpoint DocumentPill.confirmUpload uses.
     */
    static async markDocumentUploaded(
        mortgageDocumentId: string,
        mortgageId: string,
    ): Promise<void> {
        await axios.post("/MortgageDocuments/MarkMortgageDocumentAsUploaded", null, {
            params: { mortgageDocumentId, mortgageId },
        });
    }

    /**
     * Load ALL mortgage documents by calling both endpoints and merging.
     * - GetMortgageDocumentsByMortgageId: all categories, excludes Inactive status
     * - GetAllDocuments: Finance category only, includes all statuses
     * Together they give us the complete picture.
     */
    static async getAllMortgageDocuments(
        mortgageId: string,
        primaryContactId?: string,
        secondaryContactId?: string,
    ): Promise<PortalMortgageDocumentModel[]> {
        // Call multiple endpoints and merge to get the complete document picture
        const requests = [
            axios.get(`/MortgageDocuments/GetMortgageDocumentsByMortgageId?mortgageId=${mortgageId}`),
            axios.get(`/MortgageDocuments/GetAllDocuments?mortgageId=${mortgageId}`),
        ];
        if (primaryContactId) {
            requests.push(axios.get(`/MortgageDocuments/GetApplicantMortgageDocuments?mortgageId=${mortgageId}&contactId=${primaryContactId}`));
        }
        if (secondaryContactId) {
            requests.push(axios.get(`/MortgageDocuments/GetApplicantMortgageDocuments?mortgageId=${mortgageId}&contactId=${secondaryContactId}`));
        }

        const results = await Promise.all(requests);

        // Merge all results, deduplicating by mortgageDocumentId
        const docs: PortalMortgageDocumentModel[] = [];
        const seenIds = new Set<string>();
        for (const res of results) {
            for (const d of (res.data || [])) {
                if (!seenIds.has(d.mortgageDocumentId)) {
                    seenIds.add(d.mortgageDocumentId);
                    docs.push(d);
                }
            }
        }
        return docs;
    }

    /**
     * T1: Load AIP documents from the dedicated endpoint.
     * Returns all AIP-relevant documents with a normalised aipCategory field.
     */
    static async getAipDocuments(
        mortgageId: string,
    ): Promise<PortalMortgageDocumentModel[]> {
        const res = await axios.post("/agentchat/aip-documents", { mortgageId });
        return res.data || [];
    }

    /**
     * Analyse a document that's already been uploaded to SharePoint.
     * Downloads the file from SP, runs Document Intelligence + data correlation,
     * and returns agent feedback.
     */
    static async analyseDocument(
        fileUrl: string,
        applicationId: number,
        userName: string,
        applicantName?: string,
        applicantType?: string,
        applicantProfile?: object,
        category?: string,
    ): Promise<ChatAgentResponse> {
        const res = await axios.post("/agentchat/analyse", {
            applicationId,
            userName,
            fileUrl,
            applicantName,
            applicantType,
            applicantProfile,
            category,
        });
        return res.data;
    }
}
